/**
 * Coordinate Validation Utilities
 * Validates and filters geographic coordinates to prevent Mapbox errors
 */

/**
 * Check if a coordinate pair is valid
 * @param lat Latitude value
 * @param lng Longitude value
 * @returns true if coordinates are valid
 */
export function isValidCoordinate(lat: number, lng: number): boolean {
  return (
    isFinite(lat) &&
    isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180 &&
    !(lat === 0 && lng === 0) // Exclude null island
  );
}

/**
 * Validate a single coordinate value
 * @param value Coordinate value
 * @param type 'lat' or 'lng'
 * @returns true if valid
 */
export function isValidCoordinateValue(value: number, type: 'lat' | 'lng'): boolean {
  if (!isFinite(value)) return false;

  if (type === 'lat') {
    return value >= -90 && value <= 90;
  } else {
    return value >= -180 && value <= 180;
  }
}

/**
 * Filter an array of items with coordinates, removing invalid ones
 * @param items Array of items with lat/lng properties
 * @param onInvalid Optional callback for invalid items
 * @returns Array of items with valid coordinates
 */
export function filterValidCoordinates<T extends { lat: number; lng: number }>(
  items: T[],
  onInvalid?: (item: T, reason: string) => void
): T[] {
  return items.filter((item) => {
    if (!isValidCoordinate(item.lat, item.lng)) {
      if (onInvalid) {
        let reason = 'Invalid coordinates';
        if (!isFinite(item.lat) || !isFinite(item.lng)) {
          reason = 'Non-finite coordinates';
        } else if (item.lat < -90 || item.lat > 90) {
          reason = `Latitude out of bounds: ${item.lat}`;
        } else if (item.lng < -180 || item.lng > 180) {
          reason = `Longitude out of bounds: ${item.lng}`;
        } else if (item.lat === 0 && item.lng === 0) {
          reason = 'Null island (0,0) coordinates';
        }
        onInvalid(item, reason);
      }
      return false;
    }
    return true;
  });
}

/**
 * Validate GeoJSON coordinates
 * @param coordinates GeoJSON coordinate array [lng, lat]
 * @returns true if valid
 */
export function isValidGeoJSONCoordinate(coordinates: [number, number]): boolean {
  const [lng, lat] = coordinates;
  return isValidCoordinate(lat, lng);
}

/**
 * Filter GeoJSON features with invalid coordinates
 * @param features GeoJSON features
 * @returns Filtered features with valid coordinates
 */
export function filterValidGeoJSONFeatures<T extends GeoJSON.Feature>(
  features: T[]
): T[] {
  return features.filter((feature) => {
    if (feature.geometry.type === 'Point') {
      const coords = feature.geometry.coordinates as [number, number];
      return isValidGeoJSONCoordinate(coords);
    }
    return true; // Keep non-Point features
  });
}
